import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { Brand } from './kit';

const MODULES = [
  {
    title: 'Marky Agent',
    text: 'Brief an AI agent that plans, does the work and checks it, then use it to build a website, a slide deck or a research report.',
    href: '/marky',
    link: 'About Marky Agent',
  },
  {
    title: 'AI agents for your business',
    text: 'Train an agent on your own files and website, give it your look and add it to your site to answer customers.',
    href: '/chatbots',
    link: 'About AI agents',
  },
  {
    title: 'Images, video and voice',
    text: 'Make product shots, headshots and video ads with models such as Nano Banana, Seedance, Veo and Kling, and clone a voice for narration.',
    href: '/ai-video-generator',
    link: 'About the video generator',
  },
  {
    title: 'Your brand voice',
    text: 'Set up your brand voice once and use it across posts, emails and ads, so everything sounds like you.',
    href: '/templates',
    link: 'Browse 300+ AI tools',
  },
  {
    title: 'Recordings into content',
    text: 'Turn meetings, audio and video into accurate text, then into posts and articles in a few clicks.',
    href: '/ai-transcription',
    link: 'About transcription',
  },
  {
    title: 'Workflows',
    text: 'Chain the steps of a task you repeat every week into one workflow, and run it again whenever you need it.',
    href: '/ai-workflows',
    link: 'About workflows',
  },
];

export default function WorkshopsCurriculum() {
  const { ref, state } = useReveal<HTMLOListElement>(0.15);

  return (
    <section id='curriculum' className='scroll-mt-8 py-6 sm:py-8'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='100% 0%'
          halftoneReach={45}
          className='px-6 py-14 sm:px-12 sm:py-20 lg:px-16'
        >
          <SectionHeading
            title='What you’ll learn.'
            tail='On the real product, with your own work.'
            lead={
              <>
                You work on the Unlimited plan of <Brand /> for the whole
                session, so every exercise uses the full product. Team workshops
                focus on the tools your team will use most.
              </>
            }
          />

          <ol
            ref={ref}
            data-reveal={state}
            className={clsxm(
              styles.reveal,
              'mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3',
            )}
          >
            {MODULES.map(({ title, text, href, link }, index) => (
              <li
                key={title}
                className={clsxm(
                  styles.rv,
                  'flex flex-col border-t border-[#0f0f28]/10 pt-5 dark:border-white/10',
                )}
                style={delay(index * 80)}
              >
                <span className='text-sm font-semibold tabular-nums text-primary-600 dark:text-primary-400'>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3
                  className={clsxm(
                    'mt-3 text-xl font-semibold tracking-[-0.015em]',
                    tone.ink,
                  )}
                >
                  {title}
                </h3>
                <p
                  className={clsxm(
                    'mt-2 flex-1 text-[15px] leading-7',
                    tone.body,
                  )}
                >
                  {text}
                </p>
                <Link
                  href={href}
                  className='mt-4 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-primary-600 underline-offset-4 hover:underline dark:text-primary-400'
                >
                  {link}
                  <ArrowGlyph className='h-3.5 w-3.5' />
                </Link>
              </li>
            ))}
          </ol>
        </Stage>
      </div>
    </section>
  );
}
